import { useEffect, useState } from 'react';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Quote, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { resolveImageUrl } from '@/utils/imageUrl';
import { useSiteSettings } from '@/contexts/SiteSettingsContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TestimonialsPage = () => {
  const { settings } = useSiteSettings();
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API_URL}/testimonials`);
        setTestimonials(res.data);
      } catch (e) {
        console.error('Failed to fetch testimonials:', e);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      <section className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-blue-500 to-blue-700 py-20 md:py-28">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-72 h-72 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-yellow-300 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-5 tracking-tight">
            學員成功分享
          </h1>
          <p className="text-lg md:text-xl text-blue-100 leading-relaxed max-w-2xl mx-auto">
            桃李滿門 — 聽聽他們如何在智駕駛順利考取駕照
          </p>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block w-8 h-8 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
              <p className="mt-4 text-muted-foreground">載入中...</p>
            </div>
          ) : testimonials.length > 0 ? (
            <div className="space-y-8">
              {testimonials.map((t) => (
                <Card
                  key={t.id}
                  className="overflow-hidden border-0 shadow-md bg-white"
                  data-testid={`testimonial-card-${t.id}`}
                >
                  <div className="h-1.5 bg-gradient-to-r from-blue-500 to-yellow-500" />
                  <CardContent className="p-6 md:p-8">
                    <div className="flex flex-col md:flex-row gap-6">
                      <div className="flex-shrink-0 flex flex-col items-center">
                        {t.image_url && (
                          <img
                            src={resolveImageUrl(t.image_url)}
                            alt={t.student_name}
                            className="w-20 h-20 rounded-full object-cover border-4 border-blue-100 shadow-md"
                          />
                        )}
                        <h3 className="mt-3 text-lg font-bold text-gray-900">{t.student_name}</h3>
                        <p className="text-sm text-blue-600 font-medium">導師：{t.instructor_name}</p>
                        {t.course_type && (
                          <span className="mt-1 text-xs px-2 py-0.5 bg-blue-50 text-blue-600 rounded-full">
                            {t.course_type}
                          </span>
                        )}
                      </div>
                      <div className="flex-1">
                        <Quote className="h-6 w-6 text-blue-200 mb-2" />
                        <p className="text-gray-700 leading-relaxed">{t.content}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-muted-foreground">暫無學員分享</p>
            </div>
          )}

          <div className="mt-16 text-center">
            <h2 className="text-3xl font-bold mb-4">成為下一位成功學員</h2>
            <p className="text-lg text-muted-foreground mb-8">
              加入我們，讓我們幫助您順利考取駕照
            </p>
            <Button asChild size="lg" className="rounded-full bg-green-500 hover:bg-green-600 text-white px-8">
              <a href={`https://wa.me/${settings?.whatsapp || '85295858258'}`} target="_blank" rel="noopener noreferrer">
                <MessageCircle className="mr-2 h-5 w-5" />
                WhatsApp 報名
              </a>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TestimonialsPage;
